import React, { useState, useEffect } from 'react';
import ReusableTable from '../components/adminTable';
import { TableColumn, TableAction } from '../components/adminTable';
import {
    Button,
    ConfirmModal,
} from '../components/adminModals';
import {
    reviewDecisionService,
    ReviewDecision,
    GetReviewDecisionsPayload,
    cycleService,
    eventCycleService,
    Cycle,
    Category,
    categoryAdminService,
    ExportFormat,
} from '../services/adminAuth';
import { useNotification } from '../../sharedComponents/Notifications';
import Pagination from '../components/Pagination';
import { AlertCircle, Download, FileSpreadsheet } from 'lucide-react';
import { useAuth } from '../../../auth/hooks/auth';
// import ExportButton from '../../sharedComponents/ExportButton';
import XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Swal from 'sweetalert2';

function ReviewDecisionPage() {
    const { showNotification } = useNotification();
    const { user } = useAuth();
    const isCategoryAdmin = user?.role === 'thematicarealeader';

    const [decisions, setDecisions] = useState<ReviewDecision[]>([]);
    const [loading, setLoading] = useState(true);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // Confirmation modal state
    const [isAllowModalOpen, setIsAllowModalOpen] = useState(false);
    const [selectedDecision, setSelectedDecision] = useState<ReviewDecision | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    // Dropdown data
    const [cycles, setCycles] = useState<Cycle[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [activeCycleId, setActiveCycleId] = useState<number>(0);
    // For category admins: null = not yet resolved, 0 = none assigned
    const [categoryAdminCategoryId, setCategoryAdminCategoryId] = useState<number | null>(null);

    // Track whether the active cycle has been resolved so the filter
    // useEffect doesn't fire a fetch before we know the default cycleId
    const [initialized, setInitialized] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Filter state
    const [filters, setFilters] = useState({
        categoryId: 0,
        cycleId: 0,
        applicationType: 0,
        requireReconciliation: false,
    });

    const [hasInitialFetch, setHasInitialFetch] = useState(false);

    // ── Bootstrap ──────────────────────────────────────────────────────────────
    useEffect(() => {
        const init = async () => {
            await fetchDropdownData();
            setInitialized(true);
        };
        init();
    }, []);

    // ── Re-fetch on filter/page changes (debounced) ────────────────────────────
    useEffect(() => {
        if (!initialized) return;
        if (activeCycleId > 0 && !hasInitialFetch) return;

        // For category admins, block fetch until their category has been resolved
        if (isCategoryAdmin && categoryAdminCategoryId === null) return;

        // For category admins with no assigned category, never fetch
        if (isCategoryAdmin && categoryAdminCategoryId === 0) return;

        const handler = setTimeout(() => {
            fetchDecisions();
        }, 400);
        return () => clearTimeout(handler);
    }, [initialized, currentPage, pageSize, filters, categoryAdminCategoryId, activeCycleId, hasInitialFetch]);

    // ──────────────────────────────────────────────────────────────────────────

    const fetchDropdownData = async () => {
        try {
            const [cyclesRes, activeCycleRes, categoriesRes] = await Promise.all([
                cycleService.getAllCycles(),
                cycleService.getRunningCycle(),
                categoryAdminService.getCategoriesForCategoryAdmin(),
            ]);

            if (activeCycleRes.isSuccess && activeCycleRes.data) {
                const activeId = activeCycleRes.data.id;
                setActiveCycleId(activeId);
                setCycles([activeCycleRes.data]);
                setFilters(prev => ({ ...prev, cycleId: activeId }));
                fetchDecisions(activeId);
                setHasInitialFetch(true);
            } else if (cyclesRes.isSuccess && cyclesRes.data) {
                setCycles(cyclesRes.data);
                setHasInitialFetch(true);
                if (cyclesRes.data.length > 0) {
                    setFilters(prev => ({ ...prev, cycleId: cyclesRes.data[0].id }));
                }
            }

            if (categoriesRes.isSuccess) {
                const fetchedCategories: Category[] = categoriesRes.data;
                setCategories(fetchedCategories);

                if (isCategoryAdmin) {
                    if (fetchedCategories.length > 0) {
                        const assignedId = fetchedCategories[0].id;
                        setCategoryAdminCategoryId(assignedId);
                        setFilters(prev => ({ ...prev, categoryId: assignedId }));
                    } else {
                        // No category assigned — block all fetches and stop the loader
                        setCategoryAdminCategoryId(0);
                        setLoading(false);
                    }
                }
            }
        } catch (err) {
            // Ensure category admins aren't left in limbo if the request fails
            if (isCategoryAdmin) {
                setCategoryAdminCategoryId(0);
                setLoading(false);
            }
        }
    };

    const fetchDecisions = async (cycleIdOverride?: number) => {
        const cycleId = cycleIdOverride !== undefined ? cycleIdOverride : Number(filters.cycleId);

        if (!cycleId || cycleId === 0) {
            return;
        }

        setLoading(true);
        try {
            const payload: GetReviewDecisionsPayload = {
                pageNumber: currentPage,
                pageSize,
                sortBy: '',
                sortDescending: true,
                categoryId: filters.categoryId,
                cycleId: cycleId,
                applicationType: 0, // Passed as 0 as requested
                requireReconciliation: filters.requireReconciliation,
            };

            const response = await reviewDecisionService.getAllReviewDecisions(payload);
            if (response.isSuccess) {
                setDecisions(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to fetch review decisions',
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while fetching review decisions',
            });
            } finally {
            setLoading(false);
        }
    };

    // ── Filter handlers ────────────────────────────────────────────────────────

    const handleFilterChange = (field: string, value: string | number | boolean) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setFilters({
            categoryId: isCategoryAdmin ? (categoryAdminCategoryId ?? 0) : 0,
            cycleId: activeCycleId,
            applicationType: 0,
            requireReconciliation: false,
        });
        setCurrentPage(1);
    };

    // ── Pagination handlers ────────────────────────────────────────────────────

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePageSizeChange = (size: number) => { setPageSize(size); setCurrentPage(1); };

    // ── Allow reconciliation ───────────────────────────────────────────────────

    const handleAllowVerificationClick = (decision: ReviewDecision) => {
        setSelectedDecision(decision);
        setIsAllowModalOpen(true);
    };

    const handleConfirmAllowVerification = async () => {
        if (!selectedDecision) return;
        setSubmitting(true);
        try {
            const response = await reviewDecisionService.allowReconciliation(selectedDecision.submissionId);
            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: 'Verification allowed successfully', timer: 3000 });
                setIsAllowModalOpen(false);
                setSelectedDecision(null);
                await fetchDecisions();
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to allow verification' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while allowing verification' });
            } finally {
            setSubmitting(false);
        }
    };

    // ── Export Handlers ────────────────────────────────────────────────────────

    const generatePDFFile = (data: ReviewDecision[], reportTitle: string) => {
        const doc = new jsPDF('landscape');
        const pageWidth = doc.internal.pageSize.getWidth();

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.text('TECHNICAL ADVISORY GROUP (TAG)', pageWidth / 2, 15, { align: 'center' });

        doc.setFontSize(12);
        doc.text(reportTitle, pageWidth / 2, 22, { align: 'center' });

        const headers = [[
            'S/N', 'Sub. No', 'Title', 'Authors', 'Type', 'Thematic Area', 'Decision', 'Requires Reconciliation'
        ]];

        const bodyData = data.map((item, index) => [
            index + 1,
            item.submission?.submissionNumber || '—',
            item.submission?.title || '—',
            item.submission?.authors || '—',
            item.submission?.applicationType === 1 ? 'Book' : item.submission?.applicationType === 2 ? 'Journal Article' : '—',
            item.submission?.category?.name || '—',
            item.decisionType || '—',
            item.requiresVerification ? 'Yes' : 'No'
        ]);

        autoTable(doc, {
            head: headers,
            body: bodyData,
            startY: 30,
            theme: 'grid',
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [21, 128, 61], textColor: 255 },
            margin: { top: 35 }
        });

        doc.save(`${reportTitle.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    const generateExcelFile = (data: ReviewDecision[], reportTitle: string) => {
        const headers = [
            'S/N', 'Sub. No', 'Title', 'Authors', 'Type', 'Thematic Area', 'Decision', 'Requires Reconciliation'
        ];

        const bodyData = data.map((item, index) => [
            index + 1,
            item.submission?.submissionNumber || '—',
            item.submission?.title || '—',
            item.submission?.authors || '—',
            item.submission?.applicationType === 1 ? 'Book' : item.submission?.applicationType === 2 ? 'Journal Article' : '—',
            item.submission?.category?.name || '—',
            item.decisionType || '—',
            item.requiresVerification ? 'Yes' : 'No'
        ]);

        const wsData = [
            ['TECHNICAL ADVISORY GROUP (TAG)'],
            [reportTitle],
            [],
            headers,
            ...bodyData
        ];

        const worksheet = XLSX.utils.aoa_to_sheet(wsData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Decisions');

        // Basic styling
        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
        for (let C = range.s.c; C <= range.e.c; ++C) {
            const address = XLSX.utils.encode_col(C) + "3"; // Header row is at index 3 (4th row)
            if (!worksheet[address]) continue;
            worksheet[address].s = { font: { bold: true }, fill: { fgColor: { rgb: "ECECEC" } } };
        }

        XLSX.writeFile(workbook, `${reportTitle.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const handleExportData = async (format: 'excel' | 'pdf', reportTitle: string) => {
        setIsExporting(true);
        Swal.fire({
            title: `Preparing ${format.toUpperCase()}`,
            text: 'Please wait...',
            allowOutsideClick: false,
            didOpen: () => { Swal.showLoading(); }
        });

        try {
            // Fetch all data for export
            const payload: GetReviewDecisionsPayload = {
                pageNumber: 1,
                pageSize: 1000,
                sortBy: '',
                sortDescending: true,
                categoryId: filters.categoryId,
                cycleId: filters.cycleId || activeCycleId,
                applicationType: 0,
                requireReconciliation: filters.requireReconciliation,
            };

            const response = await reviewDecisionService.getAllReviewDecisions(payload);
            if (response.isSuccess && response.data?.data && response.data.data.length > 0) {
                if (format === 'excel') generateExcelFile(response.data.data, reportTitle);
                else generatePDFFile(response.data.data, reportTitle);
                Swal.fire({ icon: 'success', title: 'Export Successful', timer: 1500 });
            } else {
                Swal.fire({ icon: 'info', title: 'No Data', text: 'No records found to export.' });
            }
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Export Failed' });
        } finally {
            setIsExporting(false);
        }
    };

    const handleSendEmail = async (format: ExportFormat, recipients: string[]) => {
        const docType = format === 2 ? 'excel' : 'pdf';
        Swal.fire({
            title: 'Sending Email',
            text: 'Please wait...',
            allowOutsideClick: false,
            didOpen: () => { Swal.showLoading(); }
        });

        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 2000));

            Swal.fire({
                icon: 'success',
                title: 'Email Sent!',
                text: `The ${docType} report has been sent to ${recipients.length} recipient(s).`,
                timer: 2500
            });
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Failed to send email' });
        }
    };

    // ── Table columns ──────────────────────────────────────────────────────────

    const columns: TableColumn<ReviewDecision>[] = [
        {
            key: 'submissionNumber',
            label: 'Sub. No.',
            render: (_value, row) => (
                <span className="text-gray-500 text-sm font-mono">
                    {row.submission?.submissionNumber ?? '—'}
                </span>
            ),
        },
        {
            key: 'title',
            label: 'Title / Authors',
            render: (_value, row) => (
                <div>
                    <div className="font-medium text-gray-900 mb-0.5">{row.submission?.title ?? '—'}</div>
                    <div className="text-sm text-gray-500">{row.submission?.authors ?? '—'}</div>
                    {row.requiresVerification && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600">
                            <AlertCircle className="w-3.5 h-3.5" />
                            Reconciliation Required
                        </span>
                    )}
                </div>
            ),
        },
        {
            key: 'applicationType',
            label: 'Application Type',
            render: (_value, row) => {
                const type = row.submission?.applicationType;
                return (
                    <span className="text-gray-700 text-sm">
                        {type === 1 ? 'Book' : type === 2 ? 'Journal Article' : '—'}
                    </span>
                );
            },
        },
        // {
        //     key: 'finalScore',
        //     label: 'Avg. Score',
        //     render: (_value, row) => {
        //         const score = row.finalScore;
        //         const colorClass = score >= 75 ? 'text-green-500' : score >= 50 ? 'text-yellow-500' : 'text-red-400';
        //         return (
        //             <span className={`font-semibold text-sm ${colorClass}`}>{score}%</span>
        //         );
        //     },
        // },
        // {
        //     key: 'scoreMargin',
        //     label: 'Disparity',
        //     render: (value) => (
        //         <span className="font-medium text-sm text-gray-700">{value as number}</span>
        //     ),
        // },
        {
            key: 'decisionType',
            label: 'Decision',
            render: (value) => {
                const map: Record<string, { label: string; className: string }> = {
                    Approve: { label: 'Approve', className: 'bg-green-50 text-green-700' },
                    Reject: { label: 'Reject', className: 'bg-red-50 text-red-700' },
                    RequestRevision: { label: 'Request Revision', className: 'bg-yellow-50 text-yellow-700' },
                    OngoingReconciliation: { label: 'Ongoing Reconciliation', className: 'bg-gray-100 text-gray-800' },
                };
                const config = map[value] ?? { label: value ?? '—', className: 'bg-gray-100 text-gray-600' };
                return (
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.className}`}>
                        {config.label}
                    </span>
                );
            },
        },
    ];

    // not being used for now since we're not sure if/when the allow reconciliation action will be needed, but leaving here for easy re-add if we decide to implement it in the future

    const actions: TableAction<ReviewDecision>[] = [
        {
            variant: 'dropdown',
            onClick: () => undefined,
            dropdownItems: [
                {
                    label: 'Allow Reconciliation',
                    onClick: (row: ReviewDecision) => {
                        if (row.requiresVerification && row.decisionType !== 'OngoingReconciliation') {
                            handleAllowVerificationClick(row);
                        }
                    },
                    className: (row: ReviewDecision) =>
                        row.requiresVerification && row.decisionType !== 'OngoingReconciliation'
                            ? 'text-blue-600'
                            : 'text-gray-300 cursor-not-allowed pointer-events-none'
                }
            ],
            title: 'Actions'
        }
    ];


    const renderCategoryDropdown = () => {
        if (isCategoryAdmin) {
            // No category assigned
            if (categoryAdminCategoryId === 0 || categories.length === 0) {
                return (
                    <select
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 text-gray-400 cursor-not-allowed"
                    >
                        <option>No Thematic Area Assigned</option>
                    </select>
                );
            }
            // Category assigned — show only that one, locked
            return (
                <select
                    disabled
                    value={categories[0].id}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
                >
                    <option value={categories[0].id}>{categories[0].name}</option>
                </select>
            );
        }

        // Non-category-admin: normal dropdown
        return (
            <select
                value={filters.categoryId}
                onChange={(e) => handleFilterChange('categoryId', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
                <option value={0}>Select Thematic Area</option>
                {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
            </select>
        );
    };

    // ── Resolve empty-table message ────────────────────────────────────────────

    const emptyMessage = isCategoryAdmin && categoryAdminCategoryId === 0
        ? 'No Thematic Area Assigned to this Admin yet'
        : 'No review decisions found.';

    // ── Filter panel ───────────────────────────────────────────────────────────

    const filterPanel = (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cycle</label>
                    <select
                        value={filters.cycleId}
                        onChange={(e) => handleFilterChange('cycleId', parseInt(e.target.value))}
                        disabled={activeCycleId > 0}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                        {activeCycleId > 0 ? (
                            <option value={activeCycleId}>{cycles.find(c => c.id === activeCycleId)?.name || 'Active Cycle'}</option>
                        ) : (
                            <>
                                <option value={0}>Select Cycle</option>
                                {cycles.map((cycle) => (
                                    <option key={cycle.id} value={cycle.id}>
                                        {cycle.name}
                                    </option>
                                ))}
                            </>
                        )}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Thematic Area</label>
                    {renderCategoryDropdown()}
                </div>

                {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Application Type</label>
                    <select
                        value={filters.applicationType}
                        onChange={(e) => handleFilterChange('applicationType', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value={0}>Select Type</option>
                        <option value={1}>Book</option>
                        <option value={2}>Journal Article</option>
                    </select>
                </div> */}

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reconciliation Required</label>
                    <select
                        value={filters.requireReconciliation ? 'true' : 'false'}
                        onChange={(e) => handleFilterChange('requireReconciliation', e.target.value === 'true')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="false">All</option>
                        <option value="true">Requires Reconciliation</option>
                    </select>
                </div>
            </div>
            <button onClick={handleClearFilters} className="mt-4 text-sm text-blue-600 hover:underline">
                Clear all filters
            </button>
        </div>
    );

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <div className="w-full">
            <div className="flex flex-wrap -mx-[15px]">
                <div className="w-full px-[15px]">
                    <div className="mb-6 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                                Review Decisions
                            </h1>
                            <p className="text-gray-500 text-sm sm:text-base">
                                View final review decisions for submitted manuscripts.
                            </p>
                        </div>

                    </div>

                    <ReusableTable
                        title="Review Decisions"
                        subTitle="All reviewer decisions and reconciliation status"
                        columns={columns}
                        data={decisions}
                        // comment out actions for now since were not sure if/when the allow reconciliation action will be used
                        // actions={actions}
                        loading={loading}
                        emptyMessage={emptyMessage}
                        showSearch={false}
                        showFilter={true}
                        filterPanel={filterPanel}
                        onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
                        isFilterOpen={isFilterOpen}
                    />

                    {!loading && totalRecords > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalRecords={totalRecords}
                            pageSize={pageSize}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    )}

                    <ConfirmModal
                        isOpen={isAllowModalOpen}
                        onClose={() => { setIsAllowModalOpen(false); setSelectedDecision(null); }}
                        onConfirm={handleConfirmAllowVerification}
                        title="Allow Reconciliation"
                        message={
                            selectedDecision
                                ? `Are you sure you want to allow reconciliation for submission "${selectedDecision.submission?.submissionNumber ?? selectedDecision.submissionId}"? This will enable the reconciliation process for this manuscript.`
                                : 'Are you sure you want to allow reconciliation for this submission?'
                        }
                        confirmText="Allow"
                        cancelText="Cancel"
                        variant="info"
                        isSubmitting={submitting}
                    />
                </div>
            </div>
        </div>
    );
}

export default ReviewDecisionPage;